// @ts-nocheck
// Single-page registration editor: replaces the retired create/edit wizard.
// Left nav of SECTION PANES — only the active section renders (tracked in the
// URL as ?section=). Each pane saves independently (see useRegistrationDraft
// for the save/dirty model and the roster pane's parent tournament/league
// dual-write). Leaving a dirty pane is intercepted with the standard
// settings-page pattern (Shopify Polaris / Salesforce Lightning): Save &
// continue / Discard changes / Keep editing.
import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import helpers from '@/utils/helpers';
import { useAuth, useUser } from '@/auth/clerkShim';
import { toast } from 'sonner';
import useOrganizationStore from '@store/organizationStore';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { PageHeaderSkeleton, ListSkeleton } from '@/components/Page/PageSkeletons';
import { Loader2, ArrowLeft, TriangleAlert } from 'lucide-react';
import usePaymentProductStore from '@store/paymentProductStore';
import useWaiverStore from '@store/waiverStore';
import getRegistrationName from '@staty/shared/utils/getRegistrationName';
import useStripeConnectStatus from '@/hooks/useStripeConnectStatus';
import StripeSetupModal from '@/components/StripeSetupModal';
import useRegistrationDraft, { SECTION_IDS } from './useRegistrationDraft';
import OverviewSection from './edit-sections/OverviewSection';
import ScheduleSection from './edit-sections/ScheduleSection';
import PaymentsSection from './edit-sections/PaymentsSection';
import RosterRequirementsSection from './edit-sections/RosterRequirementsSection';
import LegalSection from './edit-sections/LegalSection';
import MessagingAccessSection from './edit-sections/MessagingAccessSection';
import FormFieldsSection from './edit-sections/FormFieldsSection';

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'schedule', label: 'Registration Access' },
  { id: 'payments', label: 'Payments' },
  { id: 'roster', label: 'Team & Roster' },
  { id: 'legal', label: 'Legal Agreements' },
  { id: 'messaging', label: 'Messages' },
  { id: 'formFields', label: 'Form Fields' },
];
const NAV_LABELS = Object.fromEntries(NAV_ITEMS.map((i) => [i.id, i.label]));
// Old two-pane form-field URLs land on the combined pane.
const LEGACY_SECTION_ALIASES = { playerFields: 'formFields', teamFields: 'formFields' };

export default function RegistrationEditPage() {
  const { registrationId } = useParams();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { leagueName, league: organization } = (useOutletContext() as any) || {};
  const d = useRegistrationDraft(registrationId);
  const [searchParams, setSearchParams] = useSearchParams();
  // The pane the admin clicked while the current one was dirty (drives the
  // unsaved-changes dialog); null = no interception in progress.
  const [pendingSection, setPendingSection] = useState(null);
  const [resolvingPending, setResolvingPending] = useState(false);

  const requestedSection = searchParams.get('section');
  const normalizedSection = LEGACY_SECTION_ALIASES[requestedSection] || requestedSection;
  const activeSection = SECTION_IDS.includes(normalizedSection) ? normalizedSection : 'overview';

  const {
    paymentProducts, getPaymentProductsByLeagueName,
    createPaymentProduct, createDonationProduct, updatePaymentProduct, getPaymentProductUsage,
  } = usePaymentProductStore((state: any) => ({
    paymentProducts: state.paymentProducts,
    getPaymentProductsByLeagueName: state.getPaymentProductsByLeagueName(getToken),
    createPaymentProduct: state.createPaymentProduct(getToken),
    createDonationProduct: state.createDonationProduct(getToken),
    updatePaymentProduct: state.updatePaymentProduct(getToken),
    getPaymentProductUsage: state.getPaymentProductUsage(getToken),
  }));
  const { waivers, fetchFinalizedWaiversByOrganization } = useWaiverStore((state: any) => ({
    waivers: state.waivers,
    fetchFinalizedWaiversByOrganization: state.fetchFinalizedWaiversByOrganization,
  }));

  useEffect(() => {
    if (leagueName) getPaymentProductsByLeagueName(leagueName);
  }, [leagueName]);
  useEffect(() => {
    if (organization?._id) fetchFinalizedWaiversByOrganization(organization._id);
  }, [organization?._id]);

  const activePaymentProducts = useMemo(
    () => paymentProducts.filter((p) => !p.deactivated && !p.isDonation),
    [paymentProducts],
  );
  const activeDonations = useMemo(
    () => paymentProducts.filter((p) => !p.deactivated && p.isDonation),
    [paymentProducts],
  );

  const registrationName = d.registration ? getRegistrationName(d.registration) : '';
  const sport = _.get(d.registration, 'sports.0', _.get(organization, 'sports.0', ''));

  // HOOKS — must stay ABOVE the !d.draft early return: on a cold URL load
  // the first render exits there, and adding hooks once the draft arrives
  // crashes React ("Rendered more hooks than during the previous render").
  // Fees/donations require a FULLY-ENABLED payment account (chargesEnabled —
  // the same signal as Get Started's money step; ADR 0045 retired the loose
  // account-exists gate that only existed for test-mode fees). Live status so
  // the Payments pane unlocks right after EMBEDDED onboarding exits (the org
  // doc from the outlet context doesn't refresh mid-page). If the status
  // endpoint is unavailable, fall back to account-exists rather than locking
  // a working org out of its fees.
  const { status: stripeStatus, refetch: refetchStripeStatus } =
    useStripeConnectStatus(organization?._id);
  const stripeAccountExists = !!(
    organization?.stripeAccountId ||
    organization?.stepsTaken?.createdStripeAccount ||
    stripeStatus?.hasAccount
  );
  const stripeConnected = stripeStatus?.error === 'unavailable'
    ? stripeAccountExists
    : !!stripeStatus?.chargesEnabled;
  const [stripeModalOpen, setStripeModalOpen] = useState(false);

  if (!d.draft) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <ListSkeleton />
      </PageContainer>
    );
  }

  const switchTo = (id) => setSearchParams({ section: id });

  const onNavClick = (id) => {
    if (id === activeSection) return;
    if (d.dirtyBySection[activeSection]) {
      setPendingSection(id);
      return;
    }
    switchTo(id);
  };

  const onSaveAndContinue = async () => {
    setResolvingPending(true);
    try {
      await d.saveSection(activeSection);
      switchTo(pendingSection);
      setPendingSection(null);
    } finally {
      setResolvingPending(false);
    }
  };

  const onDiscardAndContinue = () => {
    d.resetSection(activeSection);
    switchTo(pendingSection);
    setPendingSection(null);
  };

  // Embedded onboarding in a modal (ADR 0044) — no redirect, no lost context.
  const connectStripe = () => setStripeModalOpen(true);

  const sectionProps = { d, registrationName };
  const panes = {
    overview: <OverviewSection {...sectionProps} />,
    schedule: <ScheduleSection d={d} />,
    payments: (
      <PaymentsSection
        d={d}
        activePaymentProducts={activePaymentProducts}
        activeDonations={activeDonations}
        stripeConnected={stripeConnected}
        onConnectStripe={connectStripe}
        registrationName={registrationName}
        orgId={organization?._id}
        // The registration's own parent — excluded from the edit form's
        // "used by N other competitions" caution.
        currentParentId={helpers.getObjId(d.registration?.tournament) || helpers.getObjId(d.registration?.contest)}
        productActions={{ createPaymentProduct, createDonationProduct, updatePaymentProduct, getPaymentProductUsage }}
      />
    ),
    roster: <RosterRequirementsSection d={d} sport={sport} />,
    legal: <LegalSection d={d} waivers={waivers} registrationName={registrationName} />,
    messaging: <MessagingAccessSection d={d} registrationName={registrationName} />,
    formFields: <FormFieldsSection d={d} sport={d.sport || sport} registrationName={registrationName} />,
  };

  const navLink = (item) => (
    <button
      key={item.id}
      type="button"
      onClick={() => onNavClick(item.id)}
      className={`flex w-full shrink-0 items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors lg:shrink ${
        activeSection === item.id
          ? 'bg-primary/10 font-semibold text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
      aria-current={activeSection === item.id ? 'page' : undefined}
    >
      <span className="truncate">{item.label}</span>
      {d.dirtyBySection[item.id] && (
        <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-warning" title="Unsaved changes" />
      )}
    </button>
  );

  return (
    <PageContainer>
      <div className="space-y-3">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/admin/registration">Registrations</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/admin/registration/${registrationId}`}>{registrationName || 'Registration'}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Edit</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <PageHeader
          title="Edit Registration"
          description={registrationName}
          actions={(
            <Button variant="outline" asChild>
              <Link to={`/admin/registration/${registrationId}`}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to dashboard
              </Link>
            </Button>
          )}
        />
      </div>

      <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-8">
        {/* Section panes nav (horizontal strip on mobile) */}
        <nav className="mb-4 flex gap-1 overflow-x-auto lg:mb-0 lg:block lg:self-start lg:sticky lg:top-6 lg:space-y-1">
          {NAV_ITEMS.map(navLink)}
        </nav>

        <div className="min-w-0">{panes[activeSection]}</div>
      </div>

      {/* Unsaved-changes interception when leaving a dirty pane. */}
      <AlertDialog open={!!pendingSection} onOpenChange={(o) => { if (!o && !resolvingPending) setPendingSection(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <TriangleAlert className="h-5 w-5 text-warning" />
              You have unsaved changes
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your edits in <strong>{NAV_LABELS[activeSection]}</strong> haven&apos;t been saved. Save
              them before moving to <strong>{pendingSection ? NAV_LABELS[pendingSection] : ''}</strong>,
              or discard them to leave this section as it was.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={onDiscardAndContinue}
              disabled={resolvingPending}
            >
              Discard changes
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPendingSection(null)} disabled={resolvingPending}>
                Keep editing
              </Button>
              <Button onClick={onSaveAndContinue} disabled={resolvingPending}>
                {resolvingPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save & continue
              </Button>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <StripeSetupModal
        open={stripeModalOpen}
        onOpenChange={setStripeModalOpen}
        orgId={helpers.getObjId(organization)}
        onComplete={refetchStripeStatus}
      />
    </PageContainer>
  );
}
